import React, { useState, useRef } from 'react';
import RichEditor from './RichEditor';
import { htmlToText, tokensToRaw, appendSignature } from './RunReminders';
import { visibleFiles, resolveAttachments, fmtSize, openFile } from './fileLib';
import { api } from './api';
import Icon from './Icon';
import './MessageAgents.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A transient one-off email composer. Used for "Message Agents" and "Message Contacts".
// Nothing is persisted — the draft lives only while the tab is open.
//   recipients: [{ id, name, email, subs: { '{{Token}}': value } }]
//   variables:  [{ key, label }]  — insert chips for personalization tokens
function BroadcastComposer({
  title, subtitle, recipients = [], variables = [], fileKeys = { ownKeys: [], publicKeys: [] },
  defaultSubject = 'A message', recipientNoun = 'recipient', emptyMsg = 'No recipients yet.', onBack,
  signature = '', trackContacts = false,
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody]       = useState('');
  const [sel, setSel]         = useState(() => new Set());
  const [fileSel, setFileSel] = useState(() => new Set());
  const [status, setStatus]   = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);   // after send: [{id,name,email,status}]
  const editorRef = useRef(null);
  const lib = visibleFiles(fileKeys);

  const withEmail = recipients.filter(r => EMAIL_RE.test((r.email || '').trim()));
  const allSel = withEmail.length > 0 && withEmail.every(r => sel.has(r.id));
  const someSel = withEmail.some(r => sel.has(r.id));
  const nounPl = `${recipientNoun}s`;

  function toggle(id) { setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleAll() { setSel(allSel ? new Set() : new Set(withEmail.map(r => r.id))); }
  function toggleFile(id) { setFileSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  function reset() {
    setResults(null); setSel(new Set()); setFileSel(new Set());
    setBody(''); setSubject(''); setStatus('');
  }

  async function send() {
    setStatus('');
    const selected = recipients.filter(r => sel.has(r.id));
    if (!selected.length) { setStatus(`⚠ Select at least one ${recipientNoun}.`); return; }
    if (!htmlToText(body).trim()) { setStatus('⚠ Write a message first.'); return; }
    const subj = subject.trim() || defaultSubject;
    setSending(true); setStatus('Sending…');
    // Bytes are fetched from the server at send time (metadata-only local records).
    const attachments = await resolveAttachments([...fileSel]);
    const out = [];
    for (const r of selected) {
      const email = (r.email || '').trim();
      if (!EMAIL_RE.test(email)) { out.push({ id: r.id, name: r.name, email, status: 'skipped' }); continue; }
      let html = tokensToRaw(body);
      for (const [k, v] of Object.entries(r.subs || {})) html = html.split(k).join(v);
      html = appendSignature(html, signature);   // the sender's saved email signature
      try {
        // Authenticated send that records a SentEmail mapping when this is a contact,
        // so opens/clicks on the message credit that contact's engagement.
        await api.post('/api/broadcast/send', {
          to: email, subject: subj, html, text: htmlToText(html), attachments,
          ...(trackContacts ? { contactId: r.id } : {}),
        });
        out.push({ id: r.id, name: r.name, email, status: 'sent' });
      } catch { out.push({ id: r.id, name: r.name, email, status: 'failed' }); }
    }
    setSending(false);
    setStatus('');
    setResults(out);
  }

  return (
    <div className="ma-container">
      {onBack && <button className="ma-back" onClick={onBack}><Icon name="arrow-left" size={16} /> Back to Contacts</button>}
      <div className="ma-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      {recipients.length === 0 ? (
        <div className="ma-empty">{emptyMsg}</div>
      ) : results ? (
        <>
          {(() => {
            const sent = results.filter(r => r.status === 'sent').length;
            const failed = results.filter(r => r.status === 'failed').length;
            const skipped = results.filter(r => r.status === 'skipped').length;
            return (
              <div className="ma-result-summary">
                ✓ Sent to {sent} {sent === 1 ? recipientNoun : nounPl}
                {failed ? ` · ${failed} failed` : ''}{skipped ? ` · ${skipped} skipped (no email)` : ''}
              </div>
            );
          })()}
          <ul className="ma-recip-list ma-results">
            {results.map(r => (
              <li key={r.id} className="ma-recip ma-result-row">
                <span className={`ma-result-badge ma-${r.status}`}>
                  {r.status === 'sent' ? '✓ Sent' : r.status === 'failed' ? '✗ Failed' : '— Skipped'}
                </span>
                <span className="ma-recip-name">{r.name}</span>
                <span className="ma-recip-email">{r.email || 'no email on file'}</span>
              </li>
            ))}
          </ul>
          <div className="ma-actions">
            <button className="ma-send-btn" onClick={reset}><Icon name="edit" size={16} /> Compose another message</button>
          </div>
        </>
      ) : (
        <>
          <label className="ma-label">Subject</label>
          <input className="ma-input" placeholder={defaultSubject}
            value={subject} onChange={e => { setSubject(e.target.value); setStatus(''); }} />

          <div className="ma-label-row">
            <label className="ma-label">Message</label>
            <span className="ma-vars">
              {variables.map(v => (
                <button key={v.key} type="button" className="ma-var-chip" title={`Insert ${v.key}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => editorRef.current && editorRef.current.insertVar(v.key, v.label)}>+ {v.label}</button>
              ))}
            </span>
          </div>
          <RichEditor ref={editorRef} docKey="broadcast" value={body}
            onChange={html => { setBody(html); setStatus(''); }}
            placeholder="Write your message…" />

          {lib.length > 0 && (
            <div className="ma-files">
              <div className="ma-files-head">
                <span><Icon name="files" size={16} /> Attach files</span>
                <span className="ma-files-count">{fileSel.size} selected</span>
              </div>
              <p className="ma-files-hint">From your library and the public files shared with you.</p>
              <ul className="ma-file-list">
                {lib.map(f => (
                  <li key={f.id} className="ma-file">
                    <label className="ma-file-pick">
                      <input type="checkbox" checked={fileSel.has(f.id)} onChange={() => toggleFile(f.id)} />
                      <span className="ma-file-icon"><Icon name="file" size={16} /></span>
                      <span className="ma-file-name">{f.name}</span>
                    </label>
                    <button type="button" className="ma-file-view" title="View the file content in a new tab" onClick={() => openFile(f)}><Icon name="view" size={16} /> View</button>
                    <span className="ma-file-size">{fmtSize(f.size)}</span>
                    {f.inherited && <span className="ma-file-from">{f.owner.startsWith('mls:') ? 'MLS' : 'Org'}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="ma-recipients">
            <label className="ma-recip-all">
              <input type="checkbox" checked={allSel}
                ref={el => { if (el) el.indeterminate = someSel && !allSel; }}
                onChange={toggleAll} />
              Select all ({withEmail.length})
            </label>
            <div className="ma-recip-list">
              {recipients.map(r => {
                const hasEmail = EMAIL_RE.test((r.email || '').trim());
                return (
                  <label key={r.id} className={`ma-recip ${!hasEmail ? 'ma-recip-noemail' : ''}`}>
                    <input type="checkbox" disabled={!hasEmail}
                      checked={sel.has(r.id)} onChange={() => toggle(r.id)} />
                    <span className="ma-recip-name">{r.name}</span>
                    <span className="ma-recip-email">{hasEmail ? r.email : 'no email on file'}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="ma-actions">
            <button className="ma-send-btn" onClick={send} disabled={sending || sel.size === 0}>
              {sending ? 'Sending…' : <><Icon name="mail" size={16} /> {`Send to ${sel.size} ${sel.size === 1 ? recipientNoun : nounPl}`}</>}
            </button>
            {status && <span className="ma-status">{status}</span>}
          </div>
        </>
      )}
    </div>
  );
}

export default BroadcastComposer;
